import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, User, Briefcase, CreditCard, FileText, Clock,
  Edit2, Check, X, Phone, Mail, MapPin, AlertCircle,
  UserCheck, Building2, Calendar, ChevronDown, ChevronUp,
  Shield, Landmark, History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STATUS_COLORS = {
  alta:        "bg-emerald-100 text-emerald-800 border-emerald-200",
  vacaciones:  "bg-blue-100 text-blue-800 border-blue-200",
  incapacidad: "bg-amber-100 text-amber-800 border-amber-200",
  baja:        "bg-red-100 text-red-800 border-red-200",
};

const DOC_LABELS = {
  ine:                   "INE",
  curp:                  "CURP",
  acta_nacimiento:       "Acta de nacimiento",
  nss:                   "NSS",
  csf:                   "Constancia situación fiscal",
  comprobante_domicilio: "Comprobante de domicilio",
  comprobante_banco:     "Comprobante bancario",
  carta_recomendacion:   "Carta de recomendación",
  examen_medico:         "Examen médico",
};

const ENUM_LABELS = {
  // employment_type
  indeterminado:       "Tiempo indeterminado",
  determinado:         "Tiempo determinado",
  obra_determinada:    "Por obra determinada",
  capacitacion_inicial:"Capacitación inicial",
  // schedule_type
  tiempo_completo:     "Tiempo completo",
  medio_tiempo:        "Medio tiempo",
  por_horas:           "Por horas",
  // department
  operaciones:         "Operaciones",
  administracion:      "Administración",
  produccion:          "Producción",
  mantenimiento:       "Mantenimiento",
  // gender
  M: "Masculino", F: "Femenino", NB: "No binario",
  // marital_status
  soltero: "Soltero/a", casado: "Casado/a", union_libre: "Unión libre",
  divorciado: "Divorciado/a", viudo: "Viudo/a",
};

function fmt(val) {
  if (!val) return null;
  return ENUM_LABELS[val] || val;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-xs text-slate-400 w-40 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-800 font-medium">{value}</span>
    </div>
  );
}

export default function OpsRRHHEmpleadoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [emp, setEmp]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState("laboral");
  const [timeline, setTimeline] = useState([]);
  const [vacBalance, setVacBalance] = useState(null);
  const [creating, setCreating] = useState(false);

  const [employeeDocs, setEmployeeDocs] = useState([]);
  const [docsLoading, setDocsLoading]   = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [expiresOnDraft, setExpiresOnDraft] = useState({});
  const [managers, setManagers]         = useState([]);
  const [editingManager, setEditingManager] = useState(false);
  const [managerDraft, setManagerDraft] = useState("");
  const [savingManager, setSavingManager] = useState(false);

  const isHR = ["hr_admin", "corporate_admin"].includes(user?.role);
  const canSeeDocs = ["hr_admin", "corporate_admin", "direccion"].includes(user?.role);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/ops/employees/${id}`);
        setEmp(r.data);
        // Cargar datos adicionales en paralelo
        const [tl, vac] = await Promise.allSettled([
          api.get(`/ops/employees/${id}/timeline`),
          api.get(`/ops/vacations/balance/${id}`),
        ]);
        if (tl.status === "fulfilled") setTimeline(tl.value.data?.events || []);
        if (vac.status === "fulfilled") setVacBalance(vac.value.data);
      } catch {
        toast.error("Error al cargar colaborador");
        navigate("/ops/rrhh/empleados");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const loadDocs = async () => {
    setDocsLoading(true);
    try {
      const r = await api.get(`/ops/employees/${id}/documents`);
      setEmployeeDocs(r.data || []);
    } catch {
      // Sin permiso o error de red — el tab ya está oculto para quien no tiene acceso
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "documentos" && canSeeDocs) loadDocs();
  }, [tab, id]);

  useEffect(() => {
    if (isHR) {
      api.get(`/ops/employees?limit=200`).then(r => setManagers(r.data.employees || [])).catch(() => {});
    }
  }, [isHR]);

  const handleUploadDoc = async (docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("El archivo no puede superar 8 MB"); return; }
    setUploadingDoc(docType);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/ops/employees/${id}/documents`, {
        doc_type: docType,
        filename: file.name,
        file_base64: base64,
        expires_on: expiresOnDraft[docType] || null,
      });
      toast.success("Documento subido");
      const emp2 = await api.get(`/ops/employees/${id}`);
      setEmp(emp2.data);
      await loadDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir el documento");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const r = await api.get(`/ops/employees/${id}/documents/${doc.id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch {
      toast.error("No se pudo descargar el documento");
    }
  };

  const handleSaveManager = async () => {
    setSavingManager(true);
    try {
      await api.put(`/ops/employees/${id}`, { reports_to: managerDraft || "" });
      toast.success("Jefe directo actualizado");
      setEditingManager(false);
      const emp2 = await api.get(`/ops/employees/${id}`);
      setEmp(emp2.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingManager(false);
    }
  };

  const handleCreateUser = async () => {
    if (!window.confirm("¿Crear cuenta de portal para este colaborador?")) return;
    setCreating(true);
    try {
      const r = await api.post(`/ops/employees/${id}/create-user`);
      if (r.data.created) {
        toast.success(`Cuenta creada: ${r.data.email} / Contraseña: ${r.data.password_initial}`);
        const emp2 = await api.get(`/ops/employees/${id}`);
        setEmp(emp2.data);
      } else {
        toast.info(r.data.message || "El colaborador ya tiene cuenta");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cuenta");
    } finally {
      setCreating(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-400">
      Cargando...
    </div>
  );

  if (!emp) return null;

  const salary_fmt = emp.salary_amount
    ? `$${Number(emp.salary_amount).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN (${emp.salary_type})`
    : "—";

  const docs = emp.documents_status || {};
  const docsCompleted = Object.values(docs).filter(Boolean).length;
  const docsTotal = Object.keys(DOC_LABELS).length;

  const TABS = [
    { id: "laboral",    label: "Datos laborales", icon: Briefcase },
    { id: "personal",   label: "Datos personales", icon: User },
    { id: "compensacion", label: "Compensación", icon: CreditCard },
    ...(canSeeDocs ? [{ id: "documentos", label: "Documentos", icon: FileText }] : []),
    { id: "vacaciones", label: "Vacaciones", icon: Calendar },
    { id: "historial",  label: "Historial", icon: History },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Back */}
      <button
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        onClick={() => navigate("/ops/rrhh/empleados")}
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a colaboradores
      </button>

      {/* Hero */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-slate-900 text-white flex items-center justify-center text-xl font-bold shrink-0">
              {emp.first_name?.[0]}{emp.last_name?.[0]}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {emp.first_name} {emp.last_name}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-sm text-slate-500">{emp.employee_number}</span>
                <span className="text-slate-300">·</span>
                <span className="text-sm text-slate-600">{emp.position}</span>
                <span className="text-slate-300">·</span>
                <span className="text-sm text-slate-600">{emp.branch_name || "Sin sucursal"}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_COLORS[emp.status] || STATUS_COLORS.alta}`}>
              {emp.status?.charAt(0).toUpperCase() + emp.status?.slice(1)}
            </span>
            {isHR && (
              <div className="flex gap-2">
                {!emp.user_id && (
                  <Button
                    data-testid="create-user-btn"
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1"
                    onClick={handleCreateUser}
                    disabled={creating}
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    {creating ? "Creando..." : "Crear acceso portal"}
                  </Button>
                )}
                {emp.user_id && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> Con acceso al portal
                  </span>
                )}
                <Button
                  data-testid="edit-employee-btn"
                  size="sm"
                  variant="outline"
                  className="text-xs gap-1"
                  onClick={() => navigate(`/ops/rrhh/empleados/${id}/editar`)}
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Editar
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                tab === t.id
                  ? "bg-slate-900 text-white font-medium"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Contenido por tab */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">

        {tab === "laboral" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <Briefcase className="w-4 h-4" /> Posición
              </h3>
              <InfoRow label="Puesto" value={emp.position} />
              <InfoRow label="Código" value={emp.position_code} />
              <InfoRow label="Departamento" value={emp.department} />
              <InfoRow label="Sucursal" value={emp.branch_name} />
              <InfoRow label="Entidad legal" value={emp.legal_entity_name} />


              <InfoRow label="Tipo de empleo" value={fmt(emp.employment_type)} />
              <InfoRow label="Tipo de jornada" value={fmt(emp.schedule_type)} />
              <InfoRow label="Horas semanales" value={emp.weekly_hours ? `${emp.weekly_hours} hs` : null} />

              {isHR ? (
                <div className="mt-1">
                  {editingManager ? (
                    <div className="flex items-center gap-2 py-1.5">
                      <select
                        data-testid="reports-to-select"
                        value={managerDraft}
                        onChange={e => setManagerDraft(e.target.value)}
                        className="text-sm border border-slate-200 rounded-md px-2 py-1.5 flex-1"
                      >
                        <option value="">Sin jefe directo</option>
                        {managers.filter(m => m.id !== emp.id).map(m => (
                          <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.position})</option>
                        ))}
                      </select>
                      <button
                        data-testid="save-reports-to-btn"
                        onClick={handleSaveManager}
                        disabled={savingManager}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                      >
                        {savingManager ? "..." : "Guardar"}
                      </button>
                      <button onClick={() => setEditingManager(false)} className="text-xs text-slate-500 hover:underline">Cancelar</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <InfoRow label="Reporta a" value={emp.reports_to_name || "Sin jefe directo asignado"} />
                      <button
                        data-testid="edit-reports-to-btn"
                        onClick={() => { setManagerDraft(emp.reports_to || ""); setEditingManager(true); }}
                        className="text-xs font-semibold text-yellow-700 hover:underline shrink-0"
                      >
                        Editar
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <InfoRow label="Reporta a" value={emp.reports_to_name} />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> Fechas
              </h3>
              <InfoRow label="Fecha de ingreso" value={emp.hire_date} />
              <InfoRow label="Fecha de baja" value={emp.termination_date} />
              <InfoRow label="Alta IMSS" value={emp.imss_registration_date} />
              <InfoRow label="Clínica IMSS" value={emp.imss_clinic} />
            </div>
          </div>
        )}

        {tab === "personal" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <User className="w-4 h-4" /> Identificación
              </h3>
              <InfoRow label="CURP" value={emp.curp} />
              <InfoRow label="RFC" value={emp.rfc} />
              <InfoRow label="NSS" value={emp.nss} />
              <InfoRow label="Fecha nacimiento" value={emp.date_of_birth} />
              <InfoRow label="Género" value={fmt(emp.gender)} />
              <InfoRow label="Estado civil" value={fmt(emp.marital_status)} />
              <InfoRow label="Tipo de sangre" value={emp.blood_type} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <Phone className="w-4 h-4" /> Contacto
              </h3>
              <InfoRow label="Teléfono" value={emp.phone} />
              <InfoRow label="Email personal" value={emp.personal_email} />
              {emp.address && (
                <>
                  <InfoRow label="Calle" value={`${emp.address.street || ""} ${emp.address.exterior_number || ""}`.trim()} />
                  <InfoRow label="Colonia" value={emp.address.neighborhood} />
                  <InfoRow label="Ciudad" value={emp.address.city} />
                  <InfoRow label="Estado" value={emp.address.state} />
                  <InfoRow label="CP" value={emp.address.zip_code} />
                </>
              )}
              {emp.emergency_contact?.name && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Contacto de emergencia</p>
                  <p className="text-sm text-amber-900">{emp.emergency_contact.name}</p>
                  <p className="text-xs text-amber-700">{emp.emergency_contact.relationship} — {emp.emergency_contact.phone}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "compensacion" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <CreditCard className="w-4 h-4" /> Salario
              </h3>
              <InfoRow label="Monto" value={salary_fmt} />
              <InfoRow label="SDI" value={emp.integrated_daily_salary ? `$${Number(emp.integrated_daily_salary).toFixed(4)} MXN/día` : null} />
              <InfoRow label="Método de pago" value={emp.payment_method} />
              <InfoRow label="Régimen fiscal" value={emp.tax_regime_employee} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <Landmark className="w-4 h-4" /> Banco
              </h3>
              {emp.bank_account?.bank && (
                <>
                  <InfoRow label="Banco" value={emp.bank_account.bank} />
                  <InfoRow label="CLABE" value={emp.bank_account.clabe
                    ? `****${emp.bank_account.clabe.slice(-4)}`
                    : null
                  } />
                </>
              )}
              {emp.infonavit_credit && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <p className="font-semibold text-blue-700 mb-1">INFONAVIT</p>
                  <InfoRow label="N° Crédito" value={emp.infonavit_credit.credit_number} />
                  <InfoRow label="Tipo descuento" value={emp.infonavit_credit.discount_type} />
                  <InfoRow label="Valor" value={String(emp.infonavit_credit.discount_value)} />
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "documentos" && canSeeDocs && (
          <div className="space-y-4" data-testid="documentos-tab-content">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-slate-700">Expediente del colaborador</h3>
              <span className="text-sm text-slate-500">
                {docsCompleted}/{docsTotal} documentos
              </span>
            </div>
            <p className="text-xs text-slate-400 -mt-3">
              Dato personal sensible — visible sólo para RRHH y Dirección.
            </p>
            {docsLoading ? (
              <p className="text-sm text-slate-400">Cargando documentos...</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(DOC_LABELS).map(([key, label]) => {
                  const activeDoc = employeeDocs.find(d => d.doc_type === key && d.active);
                  const history = employeeDocs.filter(d => d.doc_type === key && !d.active);
                  return (
                    <div
                      key={key}
                      data-testid={`doc-row-${key}`}
                      className={`p-3 rounded-lg border ${activeDoc ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          {activeDoc
                            ? <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                            : <X className="w-4 h-4 text-slate-400 shrink-0" />
                          }
                          <div>
                            <p className={`text-sm font-medium ${activeDoc ? "text-emerald-800" : "text-slate-500"}`}>{label}</p>
                            {activeDoc && (
                              <p className="text-xs text-slate-500">
                                {activeDoc.original_filename} · {activeDoc.uploaded_by_name || "—"} · {activeDoc.uploaded_at?.slice(0, 10)}
                                {activeDoc.expires_on && ` · vence ${activeDoc.expires_on}`}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {activeDoc && (
                            <button
                              data-testid={`doc-view-${key}`}
                              onClick={() => handleDownloadDoc(activeDoc)}
                              className="text-xs font-semibold text-slate-600 hover:underline"
                            >
                              Ver
                            </button>
                          )}
                          <input
                            type="date"
                            title="Fecha de vencimiento (opcional)"
                            data-testid={`doc-expires-${key}`}
                            className="text-xs border border-slate-200 rounded-md px-1.5 py-1.5 w-[7.5rem]"
                            value={expiresOnDraft[key] || ""}
                            onChange={e => setExpiresOnDraft(f => ({ ...f, [key]: e.target.value }))}
                          />
                          <label
                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-md cursor-pointer transition-colors ${
                              uploadingDoc === key ? "bg-slate-200 text-slate-400" : "bg-slate-900 text-white hover:bg-slate-800"
                            }`}
                          >
                            {uploadingDoc === key ? "Subiendo..." : activeDoc ? "Reemplazar" : "Subir"}
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.webp"
                              className="hidden"
                              disabled={uploadingDoc === key}
                              data-testid={`doc-upload-${key}`}
                              onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; handleUploadDoc(key, f); }}
                            />
                          </label>
                        </div>
                      </div>
                      {history.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-slate-400 cursor-pointer" data-testid={`doc-history-toggle-${key}`}>
                            Historial ({history.length})
                          </summary>
                          <div className="mt-1 space-y-1">
                            {history.map(h => (
                              <div key={h.id} className="text-xs text-slate-400 flex justify-between">
                                <span>{h.original_filename} · {h.uploaded_at?.slice(0, 10)}</span>
                                <button onClick={() => handleDownloadDoc(h)} className="underline shrink-0 ml-2">Ver</button>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}

                {/* Otros documentos (no catalogados) */}
                <div className="p-3 rounded-lg border bg-slate-50 border-slate-200" data-testid="doc-row-otros">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-600">Otros documentos</p>
                    <label
                      className={`text-xs font-semibold px-2.5 py-1.5 rounded-md cursor-pointer transition-colors ${
                        uploadingDoc === "otros" ? "bg-slate-200 text-slate-400" : "bg-slate-900 text-white hover:bg-slate-800"
                      }`}
                    >
                      {uploadingDoc === "otros" ? "Subiendo..." : "Agregar"}
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        disabled={uploadingDoc === "otros"}
                        data-testid="doc-upload-otros"
                        onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; handleUploadDoc("otros", f); }}
                      />
                    </label>
                  </div>
                  {employeeDocs.filter(d => d.doc_type === "otros" && d.active).length === 0 ? (
                    <p className="text-xs text-slate-400">Sin documentos adicionales</p>
                  ) : (
                    <div className="space-y-1">
                      {employeeDocs.filter(d => d.doc_type === "otros" && d.active).map(d => (
                        <div key={d.id} className="text-xs text-slate-600 flex justify-between">
                          <span>{d.original_filename} · {d.uploaded_at?.slice(0, 10)}</span>
                          <button onClick={() => handleDownloadDoc(d)} className="underline shrink-0 ml-2">Ver</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "vacaciones" && (
          <div className="space-y-4">
            {vacBalance ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: "Días ganados", value: vacBalance.days_earned },
                    { label: "Días tomados", value: vacBalance.days_taken },
                    { label: "Días disponibles", value: vacBalance.days_pending, highlight: true },
                  ].map(item => (
                    <div key={item.label} className={`p-4 rounded-xl border text-center ${item.highlight ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                      <p className="text-2xl font-bold text-slate-900">{item.value}</p>
                      <p className="text-xs text-slate-500 mt-1">{item.label}</p>
                    </div>
                  ))}
                </div>
                <InfoRow label="Próximo aniversario" value={vacBalance.anniversary_date} />
                <InfoRow label="Años de servicio" value={vacBalance.years_of_service ? `${vacBalance.years_of_service} años` : null} />
              </>
            ) : (
              <p className="text-sm text-slate-400">Sin balance de vacaciones registrado</p>
            )}
          </div>
        )}

        {tab === "historial" && (
          <div className="space-y-3">
            {timeline.length === 0 ? (
              <p className="text-sm text-slate-400">Sin eventos en el historial</p>
            ) : timeline.map(ev => (
              <div key={ev.id} className="flex gap-3 items-start pb-3 border-b border-slate-100 last:border-0">
                <div className="w-2 h-2 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                <div>
                  <p className="text-sm text-slate-800">{ev.description}</p>
                  <p className="text-xs text-slate-400">{ev.created_at?.slice(0, 10)} · {ev.changed_by_name}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
